import { FullStoryHeadTag } from './fullstory-head-tag';
import { ZendeskScriptTag } from './zendesk-script-tag';
import type { TrackingScript, TrackingContext } from './types';

const SCRIPTS = [FullStoryHeadTag, ZendeskScriptTag];

const tracking = {
  injectedScriptIds: new Set<string>(),

  load(context: TrackingContext) {
    for (const script of SCRIPTS) tracking._inject(script, context);

    if (context.authenticated) {
      tracking._setup(context);
    }
  },

  _setup(context: TrackingContext) {
    for (const script of SCRIPTS) {
      if ('onAuth' in script) {
        script.onAuth(context);
      }
    }
  },

  cleanup() {
    for (const script of SCRIPTS) {
      if ('cleanup' in script) {
        script.cleanup();
      }
    }
  },

  _inject(script: TrackingScript, context: TrackingContext) {
    if (tracking.injectedScriptIds.has(script.id)) {
      tracking._remove(script);
    }

    tracking.injectedScriptIds.add(script.id);
    script.inject(tracking._build(script, context), context);
  },

  _build(script: TrackingScript, context: TrackingContext) {
    const element = document.createElement(script.tag);
    element.id = script.id;
    if (script.type != null) {
      element.setAttribute('type', script.type);
    }
    if (script.content != null) {
      // nosemgrep: javascript.browser.security.insecure-document-method.insecure-document-method
      element.innerHTML = script.content;
    }
    if (script.src != null) {
      // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
      (element as HTMLScriptElement).src = script.src;
    }

    if (script.onScriptLoad != null) {
      element.addEventListener('load', () => {
        script.onScriptLoad?.(context);
      });
    }

    return element;
  },

  _remove(script: TrackingScript) {
    script.cleanup?.();

    const element = document.getElementById(script.id);
    if (element != null) {
      element.parentNode?.removeChild(element);
    }
  },
};

export default tracking;
